import React, { useState } from 'react';
import { ArrowRight, ArrowLeft, CheckCircle, X, Zap, Sparkles, Briefcase, TrendingUp, Calendar, Baby, ShoppingBag, Award, AlertTriangle } from 'lucide-react';

export const OnboardingTutorial = ({ onComplete, selectedArchetypes, setSelectedArchetypes }) => {
  const [currentStep, setCurrentStep] = useState(0);

  const steps = [
    {
      title: 'Welcome to zero',
      content: 'zero helps you clear your inbox fast. We group emails into 8 simple categories so you can swipe through what matters.',
      icon: <Sparkles className="text-yellow-400" size={48} />,
      action: 'Next'
    },
    {
      title: 'Meet Sarah Chen',
      content: "You'll try zero as Sarah — a working parent juggling family, career, and life. These are realistic scenarios you'll recognize.",
      icon: <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white text-2xl font-bold">SC</div>,
      action: 'Next'
    },
    {
      title: 'Swipe Gestures',
      content: '',
      splitScreen: (
        <div className="grid grid-cols-2 gap-4 w-full">
          {/* Left Side - Swipe Left Actions */}
          <div className="bg-purple-500/20 backdrop-blur-md rounded-2xl p-4 border border-purple-400/30">
            <div className="flex justify-center mb-3">
              <ArrowLeft className="text-purple-400" size={40} />
            </div>
            <div className="text-center mb-4">
              <div className="text-white font-bold mb-1">Swipe Left</div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-white/90 text-sm">
                <ArrowLeft size={16} className="text-purple-300" />
                <span>Snooze</span>
              </div>
              <div className="flex items-center gap-2 text-white/90 text-sm">
                <div className="flex items-center">
                  <ArrowLeft size={16} className="text-purple-300" />
                  <ArrowLeft size={16} className="text-purple-300 -ml-2" />
                </div>
                <span>Skip</span>
              </div>
            </div>
          </div>

          {/* Right Side - Swipe Right Actions */}
          <div className="bg-green-500/20 backdrop-blur-md rounded-2xl p-4 border border-green-400/30">
            <div className="flex justify-center mb-3">
              <ArrowRight className="text-green-400" size={40} />
            </div>
            <div className="text-center mb-4">
              <div className="text-white font-bold mb-1">Swipe Right</div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-white/90 text-sm">
                <ArrowRight size={16} className="text-green-300" />
                <span>Mark Seen</span>
              </div>
              <div className="flex items-center gap-2 text-white/90 text-sm">
                <div className="flex items-center">
                  <ArrowRight size={16} className="text-green-300" />
                  <ArrowRight size={16} className="text-green-300 -ml-2" />
                </div>
                <span>Take Action</span>
              </div>
            </div>
          </div>
        </div>
      ),
      gesture: 'Short swipes are safe • Long swipes take bigger actions',
      action: 'Next'
    },
    {
      title: 'Choose Your Categories',
      content: 'Select which types of emails you want to see. You can always change this later:',
      icon: <Sparkles className="text-blue-400" size={48} />,
      categories: [
        { id: 'caregiver', icon: Baby, label: 'Family', gradient: 'from-purple-400 to-pink-500' },
        { id: 'transactional_leader', icon: Briefcase, label: 'Executive', gradient: 'from-slate-400 to-slate-600' },
        { id: 'sales_hunter', icon: TrendingUp, label: 'Sales', gradient: 'from-blue-400 to-cyan-500' },
        { id: 'project_coordinator', icon: Calendar, label: 'Projects', gradient: 'from-teal-400 to-cyan-500' },
        { id: 'enterprise_innovator', icon: Sparkles, label: 'Learning', gradient: 'from-purple-400 to-indigo-500' },
        { id: 'deal_stacker', icon: ShoppingBag, label: 'Shopping', gradient: 'from-green-400 to-emerald-500' },
        { id: 'status_seeker', icon: Award, label: 'Travel', gradient: 'from-orange-400 to-yellow-500' },
        { id: 'identity_manager', icon: AlertTriangle, label: 'Security', gradient: 'from-red-400 to-orange-500' }
      ],
      action: 'Next',
      interactive: true
    },
    {
      title: 'Ready to Swipe',
      content: "You're all set! Start with Sarah's emails and see how fast zero helps you get to done.",
      icon: <CheckCircle className="text-green-400" size={48} />,
      action: 'Get Started'
    }
  ];

  const currentStepData = steps[currentStep];
  const isLastStep = currentStep === steps.length - 1;

  const toggleArchetype = (archetypeId) => {
    setSelectedArchetypes(prev => {
      const isSelected = prev.includes(archetypeId);
      if (isSelected && prev.length === 1) {
        // Don't allow deselecting the last archetype
        return prev;
      }
      return isSelected 
        ? prev.filter(id => id !== archetypeId)
        : [...prev, archetypeId];
    });
  };

  const handleNext = () => {
    if (isLastStep) {
      onComplete();
    } else {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    onComplete();
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 p-4 overflow-hidden">
      {/* Animated gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-purple-600 to-pink-600 animate-gradient-shift" />
      
      {/* Animated orbs for depth */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/30 rounded-full blur-3xl animate-float" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-purple-500/30 rounded-full blur-3xl animate-float-delayed" />
        <div className="absolute top-1/2 right-1/3 w-64 h-64 bg-pink-500/30 rounded-full blur-3xl animate-float-slow" />
      </div>

      <style>{`
        @keyframes gradient-shift {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        @keyframes float {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(30px, -30px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
        }
        @keyframes float-delayed {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(-40px, 30px) scale(1.15); }
          66% { transform: translate(25px, -25px) scale(0.85); }
        }
        @keyframes float-slow {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(-30px, -40px) scale(1.2); }
        }
        .animate-gradient-shift {
          background-size: 200% 200%;
          animation: gradient-shift 15s ease infinite;
        }
        .animate-float {
          animation: float 20s ease-in-out infinite;
        }
        .animate-float-delayed {
          animation: float-delayed 25s ease-in-out infinite;
        }
        .animate-float-slow {
          animation: float-slow 30s ease-in-out infinite;
        }
      `}</style>

      <div className="w-full max-w-2xl bg-white/10 backdrop-blur-2xl border border-white/20 p-8 rounded-3xl shadow-2xl relative z-10">
        {/* Skip Button */}
        <button
          onClick={handleSkip}
          className="absolute top-4 right-4 text-white/60 hover:text-white/80 transition-colors"
        >
          <X size={24} />
        </button>

        {/* Progress Indicator */}
        <div className="flex justify-center mb-6">
          <div className="flex gap-2">
            {steps.map((_, index) => (
              <div
                key={index}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  index === currentStep 
                    ? 'bg-white w-6' 
                    : index < currentStep 
                      ? 'bg-green-400' 
                      : 'bg-white/30'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Step Content */}
        <div className="text-center mb-8">
          {/* Icon */}
          <div className="mb-6 flex justify-center">
            {currentStepData.icon}
          </div>

          {/* Title */}
          <h2 className="text-2xl font-bold text-white mb-4">
            {currentStepData.title}
          </h2>

          {/* Content */}
          {currentStepData.content && (
            <p className="text-white/80 leading-relaxed whitespace-pre-line mb-4">
              {currentStepData.content}
            </p>
          )}

          {/* Split Screen Layout (if present) */}
          {currentStepData.splitScreen && (
            <div className="mb-4">
              {currentStepData.splitScreen}
            </div>
          )}

          {/* Category Buttons (if present) */}
          {currentStepData.categories && (
            <div className="grid grid-cols-2 gap-2 mb-4">
              {currentStepData.categories.map((cat, idx) => {
                const Icon = cat.icon;
                const isSelected = selectedArchetypes.includes(cat.id);
                const isInteractive = currentStepData.interactive;
                
                return (
                  <button
                    key={idx}
                    onClick={() => isInteractive && toggleArchetype(cat.id)}
                    disabled={!isInteractive}
                    className={`p-3 rounded-xl flex items-center gap-2 shadow-lg transition-all ${
                      isInteractive ? 'cursor-pointer hover:scale-105' : ''
                    } ${
                      isSelected 
                        ? `bg-gradient-to-br ${cat.gradient} opacity-100` 
                        : 'bg-slate-700 opacity-40 grayscale'
                    }`}
                  >
                    <Icon size={18} className="text-white" />
                    <span className="text-white font-medium text-sm">{cat.label}</span>
                    {isInteractive && isSelected && (
                      <span className="ml-auto text-white text-xs">✓</span>
                    )}
                  </button>
                );
              })}
            </div>
          )}

          {/* Gesture Indicator */}
          {currentStepData.gesture && (
            <div className="bg-white/10 p-6 backdrop-blur-md rounded-2xl">
              <div className="text-white font-semibold text-sm">
                {currentStepData.gesture}
              </div>
            </div>
          )}
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <button
            onClick={handlePrev}
            disabled={currentStep === 0}
            className="px-4 py-2 text-white/60 hover:text-white/80 disabled:text-white/30 disabled:cursor-not-allowed transition-colors"
          >
            Previous
          </button>

          <div className="text-white/50 text-sm">
            {currentStep + 1} of {steps.length}
          </div>

          <button
            onClick={handleNext}
            className="bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white px-6 py-3 rounded-xl font-semibold flex items-center gap-2 transition-all duration-300 transform hover:scale-105"
          >
            {currentStepData.interactive 
              ? `Continue with ${selectedArchetypes.length}`
              : currentStepData.action
            }
            {isLastStep ? <Zap size={16} /> : <ArrowRight size={16} />}
          </button>
        </div>
      </div>
    </div>
  );
};
